import { useNavigate } from 'react-router-dom';

import {
  appColors,
  BackrsHeaderCard,
  BackrsListItem,
  ContentCategory,
  ContentCompetency,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { TEMPLATE_WIDTH } from '@backrs_ui/grow-ui/src/assets/styleUtils';

import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/system';

const CATEGORY_CARD_WIDTH = '211px';
const CATEGORY_CARD_HEIGHT = '81px';

type ContentCompetencyWithProficiency = ContentCompetency & { proficiency: number };

type ProsperContentProps = Readonly<{
  contentCategories: ContentCategory[];
  contentCompetenciesWithProficiencies: ContentCompetencyWithProficiency[];
}>;

export const ProsperContent = ({
  contentCategories,
  contentCompetenciesWithProficiencies,
}: ProsperContentProps) => {
  const navigate = useNavigate();

  return (
    <Box sx={columnsContainerStyle}>
      {contentCategories.map((category) => {
        const competencies = contentCompetenciesWithProficiencies.filter(
          (competency) => competency.content_category_id === category.id,
        );

        return (
          <Box key={category.id} sx={categoryContainerStyle}>
            <Box sx={categoryCardStyle}>
              <BackrsHeaderCard title={category.title} />
            </Box>
            <Box sx={competencyColumnStyle}>
              {competencies.map((competency, index) => {
                const getProficiencyForCompetency = () => {
                  switch (competency.proficiency) {
                    case 0:
                      return 'IconUncompleted';
                    case 1:
                      return 'IconLevelOne';
                    case 2:
                      return 'IconLevelTwo';
                    default:
                      return 'IconUncompleted';
                  }
                };

                return (
                  <BackrsListItem
                    key={competency.id}
                    title={competency.title}
                    onClick={() => navigate(`${APP_ROUTE.COMPETENCY}/${competency.slug}`)}
                    rightIcon={{
                      name: getProficiencyForCompetency(),
                      alt: 'Proficiency',
                    }}
                    bottomBorder={index !== competencies.length - 1}
                  />
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const columnsContainerStyle: SxProps<Theme> = {
  display: 'grid',
  gap: 2,
  pb: 2,
  gridTemplateColumns: 'repeat(1, 1fr)', // one column for smallest screens (phones)
  '@media (max-width: 600px)': {
    px: 2,
  },
  '@media (min-width: 556px)': {
    gridTemplateColumns: 'repeat(2, 1fr)', // two columns for small screens
  },
  '@media (min-width: 796px)': {
    gridTemplateColumns: 'repeat(3, 1fr)', // three columns for medium screens
  },
  [`@media (min-width: ${TEMPLATE_WIDTH})`]: {
    gridTemplateColumns: 'repeat(6, 1fr)', // all six columns for large screens
  },
};

const categoryContainerStyle: SxProps<Theme> = {
  mb: 2,
  minWidth: CATEGORY_CARD_WIDTH,
};

const categoryCardStyle: SxProps<Theme> = {
  height: CATEGORY_CARD_HEIGHT,
  mb: 3,
};

const competencyColumnStyle: SxProps<Theme> = {
  px: 2.5,
  py: 1,
  bgcolor: appColors.neutral.white,
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  display: 'flex',
  borderRadius: 4,
};
